import { Button } from '#app/components/Buttons/Button.js';
import { ModalTemplateWrapper } from '#app/components/Modals/V2/GlobalModalSystem/ModalTemplateWrapper/ModalTemplateWrapper.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { Text } from '#app/components/Primitives/Text.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { EmbedStructureVariant } from '#app/features/productizedService/embed/hooks/useEmbedVariant.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { usePageContext } from '#app/hooks/usePageContext.js';
import { BracketsIcon, EyeIcon, LockSolidIcon } from '@contra/icons/icons';
import { getUrl } from '@contra/routes';
import { AnimatePresence } from 'framer-motion';
import { ReactNode, useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const NavigationBar = styled.div`
  ${({ theme }) => css`
    width: calc(100% + 48px);
    margin-left: -24px;
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    padding: 4px 24px 4px 8px;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const Tabs = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
`;

const Tab = styled.button<{ $disabled?: boolean; $isActive?: boolean }>`
  ${({ $disabled, $isActive, theme }) => css`
    display: flex;
    flex-flow: row nowrap;
    gap: 8px;
    align-items: center;
    padding: 8px 16px;
    border-radius: 8px;
    transition: color 175ms ease;
    color: ${theme.colorsV2[$isActive ? 'black' : 'gray50']};

    &:hover {
      color: ${theme.colorsV2.black};
    }

    ${$disabled
      ? css`
          color: ${theme.colorsV2.gray40};
          &:hover {
            cursor: not-allowed;
            color: ${theme.colorsV2.gray40};
          }
        `
      : undefined}
  `}
`;

const ViewToggleContainer = styled.div`
  ${({ theme }) => css`
    border-radius: 6px;
    padding: 4px;
    height: 32px;
    background-color: ${theme.colorsV2.gray10};
    display: flex;
    flex-flow: row nowrap;
    gap: 6px;
    align-items: center;
  `}
`;

const ToggleButton = styled.button<{ $isActive: boolean }>`
  ${({ $isActive, theme }) => css`
    background: ${theme.colorsV2.gray10};
    border: 1px solid transparent;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    padding: 4px;
    height: 100%;
    color: ${theme.colorsV2.black};
    border-radius: 4px;
    transition: background-color 175ms ease;
    transition-property: background-color, border-color;

    &:hover {
      background-color: ${theme.colorsV2.gray30};
    }

    ${$isActive &&
    css`
      background-color: ${theme.colorsV2.white};
      border-color: ${theme.colorsV2.gray40};
    `}
  `}
`;

const FrameContainer = styled.div`
  padding: 32px 0;

  & iframe {
    margin: 0 auto;
  }
`;

const Textbox = styled(animated.textarea)`
  ${({ theme }) => css`
    font-family: ${theme.fonts.mono};
    color: ${theme.colorsV2.gray60};
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
    padding: 12px;
    border: 1px solid ${theme.colorsV2.gray30};
    border-radius: 8px;
    width: 100%;
    min-height: 110px;
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const getHTMLCodeString = (variant: VariantTab, slug) => {
  return `<iframe \
frameborder="0" \
height="${variant === 'horizontal' ? '172px' : '462px'}" \
src="${getUrl('/s/@slug/embed', {
    args: { slug: slug ?? '' },
    search: { structure: variant },
  })}" \
title="View this service on Contra" \
width="${variant === 'vertical' ? '384px' : '100%'}" \
/>
  `;
};

type ServicePageOwnerEmbedModalProps = {
  readonly hasCover: boolean;
  readonly isOpen: boolean;
  readonly onClose: () => void;
};

type VariantTab = EmbedStructureVariant;
type ViewMode = 'code' | 'preview';

const TABS: Array<{ id: VariantTab; label: string }> = [
  { id: 'horizontal', label: 'Horizontal' },
  { id: 'vertical', label: 'Vertical' },
];

const VIEW_MODES: Array<{ id: ViewMode; label: ReactNode }> = [
  { id: 'preview', label: <EyeIcon size={16} /> },
  { id: 'code', label: <BracketsIcon size={16} /> },
];

export const ServicePageOwnerEmbedModal = ({
  hasCover,
  isOpen,
  onClose,
}: ServicePageOwnerEmbedModalProps) => {
  const pageContext = usePageContext();
  const slug = pageContext.routeParams.slug;
  const [currentTab, setCurrentTab] = useState<VariantTab>('horizontal');
  const [mode, setMode] = useState<ViewMode>('preview');
  const copyToClipboard = useCopyToClipboard();
  const { track } = useAnalytics();

  const isActiveTab = useCallback(
    (tabQuery: VariantTab) => currentTab === tabQuery,
    [currentTab],
  );

  const isActiveMode = useCallback(
    (modeQuery: ViewMode) => modeQuery === mode,
    [mode],
  );

  const onClickTab = (tab: VariantTab) => () => {
    setCurrentTab(tab);
  };

  const onClickMode = (newMode: ViewMode) => () => {
    setMode(newMode);
  };

  const onClickCopy = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'demand_buy_now_service_embed_copy_html',
    });
    const htmlCode = getHTMLCodeString(currentTab, slug);

    void copyToClipboard(htmlCode, {
      successToast: 'Successfully copied embed code to clipboard!',
    });
  }, [copyToClipboard, currentTab, slug, track]);

  return (
    <ModalTemplateWrapper
      ariaLabel="Embed this service anywhere"
      desktopModalSize="lg"
      headerText="Embed this service anywhere"
      isOpen={isOpen}
      onClose={onClose}
      subheaderText="Choose your embed style and paste the HTML code into your personal site."
    >
      <NavigationBar>
        <Tabs>
          {TABS.map(({ id, label }) => {
            if (id === 'vertical' && !hasCover) {
              return (
                <Tooltip
                  content="Add a service cover image to unlock this embed style."
                  key={`tab-${id}`}
                >
                  <Tab
                    $disabled
                    as="div"
                  >
                    <Text textStyle="subtitleRegular">{label}</Text>
                    <LockSolidIcon size={16} />
                  </Tab>
                </Tooltip>
              );
            }

            return (
              <Tab
                $isActive={isActiveTab(id)}
                key={`tab-${id}`}
                onClick={onClickTab(id)}
              >
                <Text textStyle="subtitleRegular">{label}</Text>
              </Tab>
            );
          })}
        </Tabs>

        <ViewToggleContainer>
          {VIEW_MODES.map(({ id, label }) => (
            <ToggleButton
              $isActive={isActiveMode(id)}
              key={`mode-${id}`}
              onClick={onClickMode(id)}
            >
              {label}
            </ToggleButton>
          ))}
        </ViewToggleContainer>
      </NavigationBar>

      <FrameContainer>
        <AnimatePresence mode="wait">
          {mode === 'preview' ? (
            <animated.div
              animate="show"
              exit="hide"
              initial="hide"
              key={`${currentTab}-preview`}
              style={{ display: 'flex' }}
              variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}
            >
              <iframe
                frameBorder="0"
                height={currentTab === 'horizontal' ? '172px' : '462px'}
                src={getUrl('/s/@slug/embed', {
                  args: { slug: slug ?? '' },
                  search: { structure: currentTab },
                })}
                title="Embed preview"
                width={currentTab === 'vertical' ? '384px' : '100%'}
              />
            </animated.div>
          ) : null}

          {mode === 'code' ? (
            <Textbox
              animate="show"
              exit="hide"
              initial="hide"
              key={`${currentTab}-code`}
              style={{ display: 'flex' }}
              variants={{ hide: { opacity: 0 }, show: { opacity: 1 } }}
            >
              {getHTMLCodeString(currentTab, slug)}
            </Textbox>
          ) : null}
        </AnimatePresence>
      </FrameContainer>
      <ButtonWrapper>
        <Button
          onClick={onClickCopy}
          variant="V2TertiarySolid"
        >
          Copy HTML Code
        </Button>
      </ButtonWrapper>
    </ModalTemplateWrapper>
  );
};
